import React from "react";
import { useTheme } from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { GridInitialStateCommunity } from "@mui/x-data-grid/models/gridStateCommunity";

import withQueryWidget, { WithQueryWidgetProps } from "components/widgetParts/queryWidgetComposition/withQueryWidget";
import WidgetLayout from "components/widgetParts/WidgetLayout";
import WidgetTitle from "components/widgetParts/WidgetTitle";
import { getSingleWidgetResponseByName, getWidgetResponsesByName } from "system/widget/Widget";
import { useTranslation } from "react-i18next";
import { ColumnModelItem, ColumnRenderType } from "components/tables/columns/ColumnModel";
import numberColumn from "components/tables/columns/NumberColumn";
import distributionBarColumn from "components/tables/columns/DistributionBarColumn";
import percentColumn from "components/tables/columns/PercentColumn";
import { queryComparisonTableDefaultSettings } from "./comparisonTableUtils";
import StripedDataGrid from "components/uiElements/dataGrid/StripedDataGrid";

export const initialColumnModel = {
  columns: [
    {
      id: "_count",
      field: "_count",
      fieldType: "integer",
      headerName: "records",
      sourceField: "_count",
      renderType: ColumnRenderType.Number
    },
    {
      id: "sentiment (n)",
      field: "sentiment (n)",
      fieldType: "keyword",
      filter: ["n"],
      headerName: "negative",
      labels: ["negativo"],
      renderType: ColumnRenderType.Number,
      sourceField: "sentiment",
    },
    {
      id: "sentiment (p)",
      field: "sentiment (p)",
      fieldType: "keyword",
      filter: ["p"],
      headerName: "positive",
      labels: ["positive"],
      renderType: ColumnRenderType.Number,
      sourceField: "sentiment",
    },
    {
      id: "sentiment (0)",
      field: "sentiment (0)",
      fieldType: "keyword",
      filter: ["0"],
      headerName: "neutral",
      labels: ["neutral"],
      renderType: ColumnRenderType.Number,
      sourceField: "sentiment",
    },
  ],
};

function QueryComparisonTable(props: WithQueryWidgetProps) {
  const { i18n } = useTranslation();
  const theme = useTheme();

  const apiResponses = getWidgetResponsesByName(props.apiResponses, "query-comparison-table");
  const apiResponseInfo = getSingleWidgetResponseByName(props.apiResponses, "query-comparison-table");
  const combinedApiResponses = getWidgetResponsesByName(props.apiResponses, "query-comparison-table-combined");

  const currentState = {...queryComparisonTableDefaultSettings, ...props.widget?.data?.settings};

  const renderCellType = (renderType: ColumnRenderType) => {
    switch (renderType) {
      case "number":
        return numberColumn();
      case "distributionBar":
        return distributionBarColumn();
      case "percent":
        return percentColumn();
      default:
        return undefined;
    }
  };

  const defaultRenderColumns: GridColDef[] = initialColumnModel.columns.map((colItem: ColumnModelItem) => {
    return {
      ...colItem,
      headerName: i18n.t(colItem.headerName),
      disableColumnMenu: true,
      ...(colItem.renderType && renderCellType(colItem.renderType)),
    };
  });

  const flattenResponse = (responseBody: any) => {
    type BucketValues = { _buckets: { _key: string, _count: string, percent: number }[] };
    const flatData: Record<string, any> = {};

    for (const [key, value] of Object.entries(responseBody)) {
      if (value && typeof value === "object" && Array.isArray((value as BucketValues)._buckets)) {
        for (const bucket of (value as BucketValues)._buckets) {
          flatData[bucket._key] = bucket._count;
          flatData["percent"] = bucket.percent;
        }
      } else {
        flatData[key] = value;
      }
    }

    return flatData;
  };

  const allFlattenedData = apiResponses?.map(res => flattenResponse(res.responseBody));
  const rows = [
    ...(apiResponses?.map((res, index) => {
      return {
        id: index,
        color: res.originatingRequest?.queryRequestColor,
        query: res.queryDisplayTitle,
        responseBody: res.responseBody,
        data: allFlattenedData?.[index]
      };
    }) ?? []),
    // ...(((apiResponses && apiResponses.length > 1) && currentState.displayAllQueriesRow) ?
    //   [{
    //     id: "combined",
    //     query: i18n.t("widget-types.query-comparison-table.combined"),
    //     data: flattenResponse(combinedApiResponses?.[0].responseBody),
    //   }] : []
    // )
  ];
  
  const renderColumns = () => {
    const queryColumn: GridColDef = {
      field: "query",
      headerName: i18n.t("query"),
      minWidth: 115,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <>
            <span style={{ color: params.row.color, marginRight: 7 }}>●</span>
            {params.value}
          </>
        );
      }
    };
    
    const modelColumns: GridColDef[] = (props.widget?.data?.columnModel?.columns ?? defaultRenderColumns).map((colItem: ColumnModelItem) => {
      return {
        ...colItem,
        field: colItem.field,
        headerName: colItem.headerName,
        disableColumnMenu: true,
        ...((colItem && colItem.renderType) && (
          renderCellType(colItem.renderType)
        )),
        valueGetter: (value: number, row: Record<string, any>, col: GridColDef | ColumnModelItem) => {
          if ("id" in col && (col.id === "__count__" || col.id === "_count")) {
            return row.data?._total;
          }

          if ("filter" in col && col.filter) {
            if (col.renderType === "percent") {
              return row.data[col.filter[0]] / row.data._total;
            }

            return row.data[col.filter[0]];
          }

          // A duplicate column will have a number in its field to differentiate it from the first of its type
          // For this, we need to remove the hyphen & number like so: "comments (avg)-1" => "comments (avg)"
          return row.data?.[colItem.field.split("-")[0]];
        },
      };
    });
    
    return [queryColumn, ...(modelColumns || defaultRenderColumns)];
  };

  const modelToMUIInitialState = (): GridInitialStateCommunity => {
    const modelColumns = props.widget?.data?.columnModel?.columns;
    const orderedFields = [
      "query",
      ...(modelColumns?.map((item: ColumnModelItem) => item.field) ?? ["_count", "sentiment (n)"])
    ];

    return {
      columns: { orderedFields }
    };
  };

  return (
    <WidgetLayout
      header={
        <WidgetTitle
          alignment={currentState?.widgetTitleAlignment}
          customTitle={currentState?.customWidgetTitle}
          defaultTitle={i18n.t("widget-types.query-comparison-table.label")}
          settingsTitle={currentState?.widgetTitle ?? ""}
          style={{ pb: 2 }}
          widgetQueryTitle={apiResponseInfo?.queryDisplayTitle}
        />
      }
    >
      <StripedDataGrid
        showRowStripes={currentState.rowStripes}
        columns={renderColumns()}
        density={currentState?.density ?? "standard"}
        disableColumnMenu
        initialState={modelToMUIInitialState()}
        rows={rows}
      />
    </WidgetLayout>
  );
}

export default withQueryWidget(QueryComparisonTable, {
  hasSettingsDialog: true,
  hasQueryDialog: true
});
